import React, { ReactNode } from 'react';

import Layout from '../../Layout';
import LinkCell from '../../Table/LinkCell';
import Text from '../../Text';

/** Figma: Size — sm-64 (64px tall), lg-84 (84px tall). Both use body-sm for main text. */
export type CellSize = 'sm-64' | 'lg-84';

/** Figma: Appearance/tone — default, empty (N/A), link (Label Link) */
export type CellTone = 'default' | 'empty' | 'link';

/** Figma: R Align. start | center | end. */
export type CellAlign = 'start' | 'center' | 'end';

export interface CellProps {
  /** Figma: Label. When null/undefined/empty, renders N/A "—" with empty tone. */
  value?: string | null;
  /** Figma: Size variant (cell height). sm-64 = 64px, lg-84 = 84px. Main text is always body-sm. Default: sm-64. */
  size?: CellSize;
  /** Figma: Tone. When omitted, derived: default when value present, empty when empty. */
  tone?: CellTone;
  /** Figma: R Align. Default: start. When end, overridden to start when isFirstColumn is true. */
  align?: CellAlign;
  /** When true and align is end, render as start (e.g. first column in table). Passed by Row. */
  isFirstColumn?: boolean;
  /** Ellipsis when overflow. Default: true. */
  ellipsis?: boolean;
  semibold?: boolean;
  /** Figma: .Items / Label Icon → single leading slot (avatar, logo, or icon). */
  leadingContent?: ReactNode;
  /** Figma: Sub Icon? → trailing slot. */
  trailingContent?: ReactNode;
  /** Figma: Label Link. When set, wraps content in a link. */
  href?: string;
  /** Figma: Details line (Label+Details / Text Link+Details). Smaller line below main label. */
  supportingText?: string | null;
  /** When provided, render custom content instead of the default label/supporting text layout. */
  children?: ReactNode;
  /** When true, the inner content area does not clip overflow (e.g. for dropdowns). Default false. */
  overflowVisible?: boolean;
  /** Optional position for the content Layout (e.g. "relative" for checkbox overlay alignment). */
  position?: 'static' | 'relative' | 'absolute' | 'fixed' | 'sticky';
}

const GAP = 8;

/** Main label text size. All cell sizes use body-sm (Figma: sm-64, lg-84). */
const CELL_LABEL_TEXT_SIZE = 'body-sm';

/** Map size to cell height (px). sm-64 = 64px, lg-84 = 84px. */
const sizeToHeight: Record<CellSize, number> = {
  'sm-64': 64,
  'lg-84': 84
};

/** Map tone to Text color. Figma: default → gray-600, empty (N/A) → gray-100, link → link-palette (same as $link-blue). */
const toneToColor: Record<CellTone, 'gray-600' | 'gray-100' | 'blue-600' | 'link-palette'> = {
  default: 'gray-600',
  empty: 'gray-100',
  link: 'link-palette'
};

/** Map align to Layout justify and Text align. Figma: R Align. */
const alignToJustify: Record<CellAlign, 'flex-start' | 'center' | 'flex-end'> = {
  start: 'flex-start',
  center: 'center',
  end: 'flex-end'
};
const alignToTextAlign: Record<CellAlign, 'left' | 'center' | 'right'> = {
  start: 'left',
  center: 'center',
  end: 'right'
};

const BaseCell: React.FC<CellProps> = ({
  value,
  size = 'sm-64',
  tone: toneProp,
  align = 'start',
  isFirstColumn,
  ellipsis = true,
  semibold = false,
  leadingContent,
  trailingContent,
  href,
  supportingText,
  children,
  overflowVisible = false,
  position
}) => {
  const hasCustomContent = children !== undefined;

  // Figma: N/A — empty value → "—" and empty tone (only when not rendering custom children)
  const isEmpty = !hasCustomContent && (value === null || value === undefined || value === '');
  const displayValue = isEmpty ? '—' : (value as string);

  // Figma: tone derived when empty (empty), when href (link = blue), else default; explicit tone overrides
  const tone: CellTone = toneProp ?? (href ? 'link' : isEmpty ? 'empty' : 'default');
  const color = toneToColor[tone];
  const cellHeight = sizeToHeight[size];
  // When align is end but this is the first column in the table, show as start
  const effectiveAlign = align === 'end' && isFirstColumn ? 'start' : align;
  const justify = alignToJustify[effectiveAlign];
  const textAlign = alignToTextAlign[effectiveAlign];

  const content = (
    <Layout
      direction="row"
      gap={GAP}
      align="center"
      justify={justify}
      minWidth={0}
      height={cellHeight}
      {...(position ? { position } : {})}
    >
      {leadingContent ? (
        <Layout flexShrink={0} align="center">
          {leadingContent}
        </Layout>
      ) : null}
      <Layout direction="column" gap={0} minWidth={0} overflow={overflowVisible ? undefined : 'hidden'}>
        {hasCustomContent ? (
          children
        ) : (
          <>
            <Text size={CELL_LABEL_TEXT_SIZE} color={color} align={textAlign} ellipsis={ellipsis} semibold={semibold}>
              {displayValue}
            </Text>
            {/* Figma: Label+Details / Text Link+Details — Details line below main label */}
            {supportingText ? (
              <Text size="body-xs" color="gray-400" align={textAlign} ellipsis>
                {supportingText}
              </Text>
            ) : null}
          </>
        )}
      </Layout>
      {trailingContent ? (
        <Layout flexShrink={0} align="center">
          {trailingContent}
        </Layout>
      ) : null}
    </Layout>
  );

  // Label Link — wrap in link when href is set; use link palette (default, hover, active, disabled)
  if (href) {
    return (
      <LinkCell href={href} padding={0} color="link-palette">
        {content}
      </LinkCell>
    );
  }

  return content;
};

export default BaseCell;
